/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
} from '@mui/material'
import { useLang } from '~/hooks/useLang'
import CloseIcon from '@mui/icons-material/Close'
import { IWorkScheduleLog } from '~/interfaces/IWorkScheduleLog'
import dayjs from 'dayjs'

interface WorkScheduleLogViewModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly log: IWorkScheduleLog | null
}

const ACTION_LABELS: Record<string, string> = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  print_device_schedule: 'Print Device Schedule',
  print_user_schedule: 'Print User Schedule',
}

function InfoRow({
  label,
  value,
}: {
  readonly label: string
  readonly value?: string | number | null
}) {
  return (
    <Box sx={{ display: 'flex', mb: 1.5, alignItems: 'center' }}>
      <Typography variant="body2" fontWeight="bold" color="text.primary" sx={{ mr: 1, whiteSpace: 'nowrap' }}>
        {label}:
      </Typography>
      <Typography variant="body2" color="text.primary">
        {value ?? '-'}
      </Typography>
    </Box>
  )
}

// custom¼r schedule-related information
function ScheduleTable({ data }: { readonly data: readonly any[] | null }) {
  const { translate } = useLang()

  if (!data || data.length === 0) {
    return <Typography variant="body2" color="text.secondary">-</Typography>
  }

  const formatDate = (date: string | null | undefined) => {
    if (!date) return '-'
    return dayjs(date).format('DD/MM/YYYY HH:mm:ss')
  }

  const showPosition = (row) => {
    const getPosition = {
      Comandante: row?.device?.device_type?.label_commander
      ? row?.device?.device_type.label_commander
      : 'Comandante',
      Motorista: row?.device?.device_type?.label_driver
      ? row?.device?.device_type.label_driver
      : 'Motorista',
      Patrulheiro: row?.device?.device_type?.label_patrolmans
      ? row?.device?.device_type.label_patrolmans
      : 'Patrulheiro',
    }
    return row.crew.map((item) => `${item.user.representation_name} [${getPosition[item.position.name]}]`).join(', ')
  }


return (
  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
    <Table size="small" stickyHeader sx={{ fontSize: '0.75rem', minWidth: 600 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold' }}>{translate('Schedule')}</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>{translate('Work Shift')}</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>{translate('Start Date')}</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>{translate('End Date')}</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>{translate('Device:name')}</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>{translate('Task')}</TableCell>
          <TableCell sx={{ fontWeight: 'bold' }}>{translate('Crew')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((row: any, idx: number) => (
          <TableRow key={idx}>
            <TableCell>{row.name ?? '-'}</TableCell>
            <TableCell>{row.work_shift?.name ?? '-'}</TableCell>
            <TableCell>{formatDate(row.start_date)}</TableCell>
            <TableCell>{formatDate(row.end_date)}</TableCell>
            <TableCell>{row.device?.name ?? '-'}</TableCell>
            <TableCell>{row.task ?? '-'}</TableCell>
            <TableCell>{row.crew ? showPosition(row) : '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)
}

export default function WorkScheduleLogViewModal({
  open,
  onClose,
  log,
}: WorkScheduleLogViewModalProps) {
  const { translate } = useLang()

  if (!log) return null

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-'
    return dayjs(dateString).format('DD/MM/YYYY HH:mm:ss')
  }

  const actionLabel = log.action
    ? translate(ACTION_LABELS[log.action] ?? log.action)
    : '-'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle display="flex" alignItems="center" justifyContent="space-between">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {translate('Scale Audit Details')}
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ p: 1 }}>
          {/* General Information */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {translate('General Information')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <InfoRow
            label={translate('Responsible User')}
            value={
              log.user
                ? [log.user.representation_name, log.user.registration ? `(${log.user.registration})` : ''].filter(Boolean).join(' ')
                : undefined
            }
          />
          <InfoRow label={translate('action')} value={actionLabel} />
          <InfoRow label={translate('station')} value={log.station?.name} />
          <InfoRow label={translate('Performed at')} value={formatDate(log.created_at)} />


          {/* Schedule details */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            {translate('Action Details')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <ScheduleTable data={log.current_value as readonly any[]} />
        </Box>
      </DialogContent>

    </Dialog>
  )
}
